/**
 * The breadcrumb trail shared by the account, location and settings pages:
 *
 *   All accounts › Account › Location › Settings
 *
 * A location's settings, for someone who may open the account's settings,
 * hang under the account's settings instead — the way back there is always
 * one tap:
 *
 *   All accounts › Account › Settings › Location
 *
 * Each level shows only when the person may open it — "All accounts" for
 * someone with two or more accounts (or a system admin), the account for
 * someone who may see the account's dashboard — so a crumb is never a dead
 * end. The last crumb is the page itself and is not a link. Platforms turn
 * each crumb's `target` into their own navigation.
 */
/**
 * The crumbs for a page, root first. Levels the person may not open are left
 * out, except the page itself, which always ends the trail.
 */
export function buildNavTrail(input) {
    const { canSeeAllAccounts, account, location, settings = false, onAllAccounts = false, accountSettings } = input;
    const crumbs = [];
    if (canSeeAllAccounts || onAllAccounts)
        crumbs.push({ level: 'accounts', label: 'All accounts', target: { kind: 'accounts' } });
    if (!onAllAccounts && account) {
        const isPage = !location && !settings;
        if (account.canOpen || isPage) {
            crumbs.push({ level: 'account', label: account.name, target: { kind: 'account', accountId: account.id } });
        }
        if (location && settings && accountSettings) {
            // A location's settings under the account's: Account › Settings › Location.
            crumbs.push({
                level: 'accountSettings',
                label: 'Settings',
                standaloneLabel: `${account.name} settings`,
                target: {
                    kind: 'settings',
                    scope: 'account',
                    accountId: account.id,
                    ...(accountSettings.section && { section: accountSettings.section }),
                },
            });
            crumbs.push({
                level: 'location',
                label: location.name,
                target: { kind: 'settings', scope: 'location', accountId: account.id, locationId: location.id },
            });
            return crumbs.map((c, i) => ({ ...c, current: i === crumbs.length - 1 }));
        }
        if (location) {
            const isLocationPage = !settings;
            if (location.canOpen || isLocationPage) {
                crumbs.push({
                    level: 'location',
                    label: location.name,
                    target: { kind: 'location', accountId: account.id, locationId: location.id },
                });
            }
        }
        if (settings) {
            crumbs.push({
                level: 'settings',
                label: 'Settings',
                target: location
                    ? { kind: 'settings', scope: 'location', accountId: account.id, locationId: location.id }
                    : { kind: 'settings', scope: 'account', accountId: account.id },
            });
        }
    }
    return crumbs.map((c, i) => ({ ...c, current: i === crumbs.length - 1 }));
}
/** A trail of one crumb (the page alone) says nothing: callers hide it. */
export function navTrailWorthShowing(crumbs) {
    return crumbs.length > 1;
}
